import type { ReactNode, TableHTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";

import { cn } from "./cn";

export function PageHeader({
  title,
  description,
  actions,
  back,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** A link to the parent page, shown above the title (the app passes its router link). */
  back?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        {back && <div className="mb-2">{back}</div>}
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm text-slate-600">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  title,
  description,
  actions,
  children,
  className,
  padded = true,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={cn("min-w-0 rounded-2xl border border-slate-300 bg-white shadow-xs", className)}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            {title && <h2 className="text-[0.95rem] font-bold text-slate-900">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? "px-5 py-4" : undefined}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, hint, icon, children }: {
  label: ReactNode; value: ReactNode; hint?: ReactNode; icon?: ReactNode; children?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-300 bg-white px-5 py-4 shadow-xs">
      {icon ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          {icon}
        </div>
      ) : <p className="text-sm font-medium text-slate-500">{label}</p>}
      <p className="mt-1 text-[1.75rem] font-bold tracking-tight text-slate-900 tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      {children}
    </div>
  );
}

/** Horizontally scrollable table so wide data never breaks the page layout. */
export function Table({ className, stack, ...rest }: TableHTMLAttributes<HTMLTableElement> & {
  /** On phones each row becomes a card line; mark the status cell with data-end. */
  stack?: boolean;
}) {
  return (
    <div className="relative overflow-x-auto">
      <table className={cn("w-full min-w-[36rem] divide-y divide-slate-200 text-sm", stack && "stack-table", className)} {...rest} />
    </div>
  );
}

export function Th({ className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        "px-4 py-2.5 text-left text-xs font-semibold whitespace-nowrap text-slate-500",
        className,
      )}
      {...rest}
    />
  );
}

export function Td({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("px-4 py-3 align-top text-slate-800", className)} {...rest} />;
}

export function DescriptionList({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-[max-content_minmax(0,1fr)]">
      {items.map(([term, detail], index) => (
        <div key={index} className="contents">
          <dt className="text-slate-500">{term}</dt>
          <dd className="min-w-0 break-words text-slate-900">{detail}</dd>
        </div>
      ))}
    </dl>
  );
}
